import type { CollectibleId } from "@/services/collectibles/collectiblesTypes";
import type { CategoryId } from "@/services/collection/categories/categoriesSchema";
import { createCollectionItem, updateCollectionItem } from "@/services/collection/item/collectionItemApi";
import type { CollectionItem } from "@/services/collection/item/collectionItemTypes";
import { uploadImage } from "@/services/collection/item/gallery";
import { batchSetCollectionItemParameters, type ParameterSetRequest } from "@/services/parameters/parametersApi";

type Photos = { front: File; back?: File };

export type CreateCollectionItemFromCollectibleOptions = {
  collectibleId: CollectibleId;
  categoryId: CategoryId;
  photos?: Photos;
  aiIdentificationSourceId?: string;
  publicNote?: string;
  primaryParams?: ParameterSetRequest[];
};

export const createCollectionItemFromCollectible = async (options: CreateCollectionItemFromCollectibleOptions): Promise<CollectionItem> => {
  const item = await createCollectionItem({
    categoryId: options.categoryId,
    collectibleId: options.collectibleId,
    set: options.collectibleId,
    minifigure: options.collectibleId,
    userCollectionListName: "",
  });

  const tasks: Promise<unknown>[] = [];

  if (options.photos) {
    tasks.push(uploadImage(options.photos.front, item, { suppressToast: true }));
    if (options.photos.back) tasks.push(uploadImage(options.photos.back, item, { suppressToast: true }));
  }

  if (options.aiIdentificationSourceId !== undefined) {
    tasks.push(updateCollectionItem(item.id, { aiIdentificationSourceId: options.aiIdentificationSourceId }));
  }

  if (options.publicNote !== undefined) {
    tasks.push(updateCollectionItem(item.id, { publicNote: options.publicNote }));
  }

  if (options.primaryParams && options.primaryParams.length > 0) {
    tasks.push(batchSetCollectionItemParameters(item.id, options.primaryParams));
  }

  await Promise.all(tasks);
  return item;
};
